export interface PowerBIEmbedRequest {
  reportsURL: string;
  workspaceId: string;
  reportId: string;
  jwtToken: string;
  dealerAcctId: string;
  datasetId: string;
  roles: string;
}

type PowerBIEmbedResponse = {
  embedUrl?: string;
  token?: string;
  accessToken?: string;
  reportId?: string;
  expiration?: string;
  expiresOn?: string;
  tokenType?: number;
  pageName?: string;
  filters?: string[];
};

function normalizeEmbedResponse(
  resObj: PowerBIEmbedResponse
): PowerBIEmbedResponse {
  const token = resObj.token ?? resObj.accessToken;
  if (!token) {
    throw new Error("Invalid PowerBI response: missing embed token");
  }

  return {
    embedUrl: resObj.embedUrl,
    token,
    accessToken: token,
    reportId: resObj.reportId,
    expiration: resObj.expiration,
    expiresOn: resObj.expiresOn,
    tokenType: resObj.tokenType,
    pageName: resObj.pageName,
    filters: resObj.filters,
  };
}

async function parseJsonBody(res: Response): Promise<PowerBIEmbedResponse> {
  const text = await res.text();
  try {
    return text ? (JSON.parse(text) as PowerBIEmbedResponse) : {};
  } catch {
    throw new Error("Invalid PowerBI response (non-JSON)");
  }
}

async function getFallbackGET(
  reportsURL: string,
  qsObj: Record<string, string>,
  jwtToken?: string
): Promise<Response> {
  const url = `${reportsURL}?${new URLSearchParams(qsObj).toString()}`;
  const headers: Record<string, string> = { Accept: "application/json" };
  if (jwtToken) headers.Authorization = `Bearer ${jwtToken}`;
  return fetch(url, { method: "GET", headers, credentials: "include" });
}

export async function getPowerBIEmbedInfo(
  req: PowerBIEmbedRequest
): Promise<PowerBIEmbedResponse> {
  const {
    reportsURL,
    workspaceId,
    datasetId,
    reportId,
    jwtToken,
    dealerAcctId,
    roles,
  } = req;

  const postHeaders: Record<string, string> = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
  if (jwtToken) postHeaders.Authorization = `Bearer ${jwtToken}`;

  const payload = { workspaceId, datasetId, reportId, dealerAcctId, roles };
  const postRes = await fetch(reportsURL, {
    method: "POST",
    headers: postHeaders,
    body: JSON.stringify(payload),
    credentials: "include",
  });

  if (postRes.status === 405) {
    const getRes = await getFallbackGET(
      reportsURL,
      { workspaceId, reportId, dealerAcctId, datasetId, roles },
      jwtToken
    );
    if (!getRes.ok) {
      const errText = await getRes.text().catch(() => "");
      throw new Error(
        `PowerBI GET failed: ${getRes.status} ${getRes.statusText} ${errText}`
      );
    }
    const resObj = await parseJsonBody(getRes);
    return normalizeEmbedResponse(resObj);
  }

  if (!postRes.ok) {
    const errText = await postRes.text().catch(() => "");
    throw new Error(
      `PowerBI POST failed: ${postRes.status} ${postRes.statusText} ${errText}`
    );
  }

  const resObj = await parseJsonBody(postRes);
  return normalizeEmbedResponse(resObj);
}
